import React, { useState } from 'react';
import cn from 'classnames';
import {
  attributesForContactInfo,
  defaultLabelForSource,
  defaultLabelOptionsForSource,
} from '@models/Contact';
import { DropdownMenuOption, ID } from '@app/types';
import {
  ActionCopyIcon,
  ChevronRightIcon,
  EmailSourceIcon,
  PhoneIcon,
  SpeechBubbleIcon,
} from '@components/Icons';
import DropdownMenu, { EllipsisToXIcon } from '@components/Menus/DropdownMenu';
import SourceLabelEditor from '../LabelEditor';
import styles from './ActionsMenu.module.scss';
import SourceLabelOption from '../SourceLabelOption';
import { useDispatch } from 'react-redux';
import { ContactSource } from '@reducers/data/sources';
import { updateSourceLabel } from '@actions/sources';
import { alertUser } from '@actions/alerts';

export default function SourceActionsMenu({
  contactID,
  source,
  triggerClassName,
}: {
  contactID: ID;
  source: ContactSource;
  triggerClassName: string;
}) {
  const dispatch = useDispatch();
  const { label, href } = attributesForContactInfo(source, true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingNewLabel, setEditingNewLabel] = useState(false);
  const [newLabel, setNewLabel] = useState('');

  const defaultOptions = defaultLabelOptionsForSource(source.type).map((defaultLabelOption) => {
    return {
      title: (
        <SourceLabelOption selected={label === defaultLabelOption}>
          {defaultLabelOption}
        </SourceLabelOption>
      ),
      action: () => {
        if (defaultLabelForSource(source.type) === defaultLabelOption) {
          dispatch(updateSourceLabel({ contactID, sourceID: source.id, label: null }));
        } else {
          dispatch(
            updateSourceLabel({ contactID, sourceID: source.id, label: defaultLabelOption }),
          );
        }
      },
      keepMenuOpenOnClick: true,
    };
  });

  const options: DropdownMenuOption[] = [
    {
      title: (
        <span className={styles.changeLabelOption}>
          Change label <ChevronRightIcon width="10" height="10" />
        </span>
      ),
      submenu: [
        ...defaultOptions,
        {
          divider: true,
        },
        {
          title: (
            <SourceLabelEditor
              contactID={contactID}
              sourceID={source.id}
              editing={editingNewLabel}
              setEditing={setEditingNewLabel}
              value={newLabel}
              setValue={setNewLabel}
            />
          ),
          keepMenuOpenOnClick: true,
          action: () => {},
          className: styles.sourceLabelEditor,
        },
      ],
    },
    {
      divider: true,
    },
    {
      title: 'Copy',
      action: () => {
        navigator.clipboard.writeText(source.value);
        dispatch(alertUser('Copied source to clipboard!', 'success'));
      },
      icon: ActionCopyIcon,
    },
    source.type === 'phone' && {
      title: 'Call',
      action: () => {
        window.location.href = `tel:${source.value}`;
      },
      icon: PhoneIcon,
    },
    source.type === 'phone' && {
      title: 'Text',
      action: () => {
        window.location.href = `sms:${source.value}`;
      },
      icon: SpeechBubbleIcon,
    },
    source.type === 'email' && {
      title: 'Email',
      action: () => {
        window.location.href = href;
      },
      icon: EmailSourceIcon,
    },
  ].filter(Boolean) as DropdownMenuOption[];

  return (
    <DropdownMenu
      triggerProps={{ children: <EllipsisToXIcon isOpen={menuOpen} width="12" height="12" /> }}
      options={options}
      suppressFocusControlOnItems
      onClose={() => {
        setEditingNewLabel(false);
        setNewLabel('');
        setMenuOpen(false);
      }}
      onOpen={() => setMenuOpen(true)}
      menuClassName={styles.sourceLabelMenu}
      triggerClassName={cn(
        styles.sourceOverflowMenuTriggerBtn,
        triggerClassName,
        menuOpen && styles.menuOpen,
      )}
    />
  );
}
